import { AuthService } from './authService';
import { TransitPackagingDto } from '../types/shipmentLine';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class TransitPackagingService {
  /**
   * Hent alle emballasjetyper for gjeldende tenant
   */
  async getAll(): Promise<TransitPackagingDto[]> {
    const url = `${API_BASE_URL}/transit-packaging`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch transit packaging: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Hent en emballasjetype basert på ID
   */
  async getById(transitPackagingId: number): Promise<TransitPackagingDto> {
    const url = `${API_BASE_URL}/transit-packaging/${transitPackagingId}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch transit packaging: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Hent standard emballasjetype
   */
  async getDefault(): Promise<TransitPackagingDto | null> {
    const url = `${API_BASE_URL}/transit-packaging/default`;
    const response = await AuthService.authenticatedGet(url);

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch default transit packaging: ${response.statusText}`);
    }

    return response.json();
  }

  // ─── CRUD for lastbærer-administrasjon (Teamwork 36334219) ───

  async create(request: SaveTransitPackagingRequest): Promise<TransitPackagingDto> {
    const url = `${API_BASE_URL}/transit-packaging`;
    const response = await AuthService.authenticatedPost(url, request);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.error || `Failed to create transit packaging: ${response.statusText}`);
    }

    return response.json();
  }

  async update(transitPackagingId: number, request: SaveTransitPackagingRequest): Promise<TransitPackagingDto> {
    const url = `${API_BASE_URL}/transit-packaging/${transitPackagingId}`;
    const response = await AuthService.authenticatedPut(url, request);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.error || `Failed to update transit packaging: ${response.statusText}`);
    }

    return response.json();
  }

  /** Setter én lastbærer som standard — fjerner default-flagget fra alle andre. */
  async setDefault(transitPackagingId: number): Promise<void> {
    const url = `${API_BASE_URL}/transit-packaging/${transitPackagingId}/set-default`;
    const response = await AuthService.authenticatedPost(url, undefined);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.error || `Failed to set default: ${response.statusText}`);
    }
  }

  /** Sletter en lastbærer. Backend avviser (409) hvis den er i bruk på forsendelseslinjer. */
  async delete(transitPackagingId: number): Promise<void> {
    const url = `${API_BASE_URL}/transit-packaging/${transitPackagingId}`;
    const response = await AuthService.authenticatedDelete(url);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.error || `Failed to delete transit packaging: ${response.statusText}`);
    }
  }
}

export interface SaveTransitPackagingRequest {
  transitPackagingDescr: string;
  /** Egenvekt/tara i kg. */
  weight?: number | null;
  volume?: number | null;
  height?: number | null;
  width?: number | null;
  length?: number | null;
  /** ERP-artikkelkobling for fakturering av lastbærere. */
  externalID?: string | null;
}

export const transitPackagingService = new TransitPackagingService();
